import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles, Maximize2, ArrowUpRight, ArrowRight,
  Cloud, Palette, Users, LayoutGrid, Type, Image as ImageIcon,
  Eraser, Layers,
  LayoutTemplate, Shapes, QrCode, BarChart3, Upload, Bookmark,
} from 'lucide-react';
import Header from '../components/Layout/Header';
import Footer from '../components/Layout/Footer';
import AuthModal from '../components/Layout/AuthModal';
import CreateDesignModal from '../components/Layout/CreateDesignModal';
import usePageMeta from '../lib/usePageMeta';

const AUTH_TOKEN_KEY = 'ivisora_auth_token';
const USER_KEY = 'ivisora_user';

/* ───────────── Hero — backdrop layers ─────────────────────────────────────── */

const HeroBackdrop = () => (
  <div aria-hidden className="absolute inset-0 overflow-hidden pointer-events-none">
    {/* Soft radial wash, anchored top-right */}
    <div
      className="absolute -top-40 -right-32 w-[720px] h-[720px] rounded-full blur-3xl opacity-60"
      style={{ background: 'radial-gradient(closest-side, rgba(167,139,250,0.55), rgba(244,114,182,0.25) 45%, transparent 70%)' }}
    />
    {/* Cool counter-tone, bottom-left */}
    <div
      className="absolute -bottom-40 -left-24 w-[560px] h-[560px] rounded-full blur-3xl opacity-50"
      style={{ background: 'radial-gradient(closest-side, rgba(96,165,250,0.45), rgba(167,139,250,0.18) 50%, transparent 75%)' }}
    />
    {/* Three drifting blurred orbs */}
    <div className="absolute top-24 left-[12%] w-44 h-44 rounded-full blur-3xl opacity-50 hp-drift-1" style={{ background: 'radial-gradient(closest-side, rgba(167,139,250,0.7), transparent)' }} />
    <div className="absolute top-1/2 right-[8%] w-56 h-56 rounded-full blur-3xl opacity-45 hp-drift-2" style={{ background: 'radial-gradient(closest-side, rgba(244,114,182,0.65), transparent)' }} />
    <div className="absolute bottom-16 left-1/3 w-40 h-40 rounded-full blur-3xl opacity-40 hp-drift-3" style={{ background: 'radial-gradient(closest-side, rgba(125,211,252,0.6), transparent)' }} />
    {/* Dot grid + grain */}
    <div className="absolute inset-0 hp-dotgrid" />
    <div className="absolute inset-0 hp-grain" />
  </div>
);

/* ───────────── Hero — canvas mockup (the centerpiece) ─────────────────────── */

/* Match the real LeftToolbar (frontend/src/components/Editor/LeftToolbar.jsx):
 * 9 panels, icon + label, with the active panel highlighted in violet.
 * The mockup version is scaled down — same shape, smaller dimensions.
 */
const EDITOR_TOOLBAR = [
  { id: 'templates', icon: LayoutTemplate, label: 'Templates' },
  { id: 'elements',  icon: Shapes,         label: 'Elements'  },
  { id: 'text',      icon: Type,           label: 'Text'      },
  { id: 'media',     icon: ImageIcon,      label: 'Media'     },
  { id: 'qrcode',    icon: QrCode,         label: 'QR Code'   },
  { id: 'charts',    icon: BarChart3,      label: 'Charts'    },
  { id: 'brand',     icon: Bookmark,       label: 'Brand'     },
  { id: 'color',     icon: Palette,        label: 'Background'},
  { id: 'upload',    icon: Upload,         label: 'Upload'    },
];

const EditorRailItem = ({ icon: Icon, label, active }) => (
  <div
    className={`w-12 h-11 rounded-md flex flex-col items-center justify-center gap-0.5 transition-colors ${
      active
        ? 'bg-violet-100 text-violet-700'
        : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'
    }`}
  >
    <Icon className="w-3.5 h-3.5" />
    <span className="text-[7.5px] font-semibold tracking-tight leading-none">{label}</span>
  </div>
);


const FloatingAICard = ({ icon: Icon, title, body, className = '', animationClass = 'hp-float-y', accentClass = 'from-violet-500 to-fuchsia-500' }) => (
  <div className={`absolute ${className} ${animationClass}`}>
    <div
      className="backdrop-blur-xl bg-white/85 border border-white/70 rounded-2xl px-3.5 py-3 w-[210px]"
      style={{
        boxShadow:
          '0 1px 0 0 rgba(255,255,255,0.9) inset, 0 30px 60px -22px rgba(76,29,149,0.45), 0 10px 24px -10px rgba(15,23,42,0.18)',
      }}
    >
      <div className="flex items-center gap-2.5">
        <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${accentClass} flex items-center justify-center text-white shadow-sm`}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        <div className="text-[11px] font-semibold text-slate-700 tracking-wide uppercase">{title}</div>
      </div>
      <div className="mt-2 text-[12.5px] text-slate-600 leading-snug">{body}</div>
    </div>
  </div>
);

/* The "wow" generation card — bigger, with prompt + progress + thumbnail strip */
const AIGenerationCard = ({ className = '', animationClass = '' }) => (
  <div className={`absolute ${className} ${animationClass}`}>
    <div
      className="backdrop-blur-xl bg-white/90 border border-white/70 rounded-2xl px-4 py-3.5 w-[256px]"
      style={{
        boxShadow:
          '0 1px 0 0 rgba(255,255,255,0.95) inset, 0 36px 70px -22px rgba(76,29,149,0.55), 0 14px 28px -12px rgba(15,23,42,0.22)',
      }}
    >
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-fuchsia-500 to-violet-500 flex items-center justify-center text-white shadow-sm">
          <Sparkles className="w-3.5 h-3.5" />
        </div>
        <div className="flex-1">
          <div className="text-[11px] font-semibold text-slate-700 tracking-wide uppercase">Generating image</div>
          <div className="text-[10.5px] text-slate-400 mt-0.5">Style · soft pastel</div>
        </div>
        <div className="text-[10.5px] font-semibold text-violet-600">78%</div>
      </div>
      {/* Progress bar */}
      <div className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full rounded-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500"
          style={{ width: '78%' }}
        />
      </div>
      {/* Mini variation strip — 3 done, 1 still building */}
      <div className="mt-3 grid grid-cols-4 gap-1.5">
        <div className="aspect-square rounded-md" style={{ background: 'conic-gradient(from 200deg at 50% 50%, #fde68a, #f9a8d4, #c4b5fd, #fde68a)' }} />
        <div className="aspect-square rounded-md" style={{ background: 'conic-gradient(from 80deg at 50% 50%, #c4b5fd, #93c5fd, #fbcfe8, #c4b5fd)' }} />
        <div className="aspect-square rounded-md" style={{ background: 'conic-gradient(from 0deg at 50% 50%, #fbcfe8, #fde68a, #c4b5fd, #fbcfe8)' }} />
        <div className="aspect-square rounded-md bg-slate-100 border border-slate-200 flex items-center justify-center">
          <span className="block w-3 h-3 rounded-full border-2 border-violet-400 border-t-transparent animate-spin" />
        </div>
      </div>
    </div>
  </div>
);

const HeroCanvasMockup = () => (
  // Anchored slightly left of its column on lg+ so the hero composition
  // reads balanced against the heading. On mobile, centered as before.
  <div className="relative mx-auto lg:mx-0 lg:-ml-4 w-[88%] max-w-[620px] aspect-[5/4]">
    {/* Layered glow stack behind the editor — three soft passes at different scales */}
    <div aria-hidden className="absolute -inset-3 -z-10 rounded-[2.25rem] blur-2xl opacity-70"
         style={{ background: 'radial-gradient(closest-side, rgba(167,139,250,0.45), transparent 70%)' }} />
    <div aria-hidden className="absolute inset-6 -z-10 rounded-[2rem] blur-3xl opacity-90"
         style={{ background: 'radial-gradient(closest-side, rgba(244,114,182,0.40), transparent 70%)' }} />
    <div aria-hidden className="absolute -bottom-6 left-10 right-10 h-12 -z-10 rounded-full blur-2xl opacity-50"
         style={{ background: 'radial-gradient(ellipse at center, rgba(15,23,42,0.18), transparent 70%)' }} />
    {/* gentle top sheen on the editor */}
    <div aria-hidden className="absolute -top-px left-12 right-12 h-px -z-10 opacity-80"
         style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.9), transparent)' }} />


    {/* Editor frame — multi-layer shadow + inner highlight border */}
    <div
      className="absolute inset-0 rounded-[1.75rem] bg-white overflow-hidden"
      style={{
        boxShadow:
          '0 0 0 1px rgba(255,255,255,0.9) inset, 0 1px 0 0 rgba(255,255,255,0.9) inset, 0 40px 90px -30px rgba(76,29,149,0.45), 0 18px 40px -18px rgba(15,23,42,0.22), 0 6px 16px -8px rgba(15,23,42,0.14)',
        border: '1px solid rgba(226,232,240,0.7)',
      }}
    >
      {/* Window bar */}
      <div className="h-9 flex items-center gap-3 px-4 border-b border-slate-100 bg-gradient-to-b from-white to-slate-50/60 backdrop-blur">
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-rose-300 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]" />
          <div className="w-2.5 h-2.5 rounded-full bg-amber-300 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]" />
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-300 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]" />
        </div>
        <div className="ml-3 flex-1 max-w-[240px] h-5 rounded-full bg-slate-100/80 border border-slate-200/60 px-2.5 flex items-center text-[10px] text-slate-500 font-medium">ivisora.app/editor</div>
        <div className="ml-auto flex items-center gap-2.5">
          <div className="text-[10px] text-slate-500 font-medium hidden sm:block">1080 × 1080</div>
          <div
            className="px-2.5 h-5 rounded-full text-white text-[10px] font-semibold flex items-center shadow"
            style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7 50%, #ec4899)' }}
          >
            Share
          </div>
        </div>
      </div>

      <div className="flex h-[calc(100%-2.25rem)]">
        {/* Left tool rail — mirrors the real LeftToolbar (Templates / Elements / Text /
            Media / QR Code / Charts / Brand / Background / Upload). 'Text' is active. */}
        <div className="w-[60px] border-r border-slate-100 bg-gradient-to-b from-white to-slate-50/40 flex flex-col items-center gap-0.5 py-2">
          {EDITOR_TOOLBAR.map(item => (
            <EditorRailItem key={item.id} icon={item.icon} label={item.label} active={item.id === 'text'} />
          ))}
        </div>

        {/* Canvas surface — soft workbench gradient + dot grid for realism.
            Tightened padding so the artwork commands the visual space. */}
        <div
          className="relative flex-1 flex items-center justify-center p-2 sm:p-3"
          style={{
            background:
              'radial-gradient(ellipse at 30% 20%, #ffffff 0%, #f4f5fb 55%, #e9ecf5 100%)',
          }}
        >
          {/* faint editor grid */}
          <div
            aria-hidden
            className="absolute inset-0 opacity-50"
            style={{
              backgroundImage:
                'radial-gradient(circle, rgba(15,23,42,0.06) 1px, transparent 1px)',
              backgroundSize: '14px 14px',
            }}
          />

          {/* Active canvas — editorial "Atelier" poster.
              Selected element is the hero image (right side), not text. */}
          <div className="relative w-full max-w-[360px]">
            <div aria-hidden className="absolute -inset-3 rounded-3xl blur-2xl opacity-50 -z-10"
                 style={{ background: 'radial-gradient(closest-side, rgba(167,139,250,0.55), transparent 70%)' }} />
            <div
              className="relative aspect-square rounded-2xl bg-white overflow-hidden"
              style={{
                boxShadow:
                  '0 0 0 1px rgba(255,255,255,0.7) inset, 0 24px 50px -22px rgba(15,23,42,0.32), 0 6px 16px -8px rgba(15,23,42,0.14)',
              }}
            >
              {/* Background — layered cream/lavender gradient + soft blooms */}
              <div className="absolute inset-0"
                   style={{ background: 'linear-gradient(135deg, #fdf6ec 0%, #faf3f8 45%, #f1ecf6 100%)' }} />
              <div aria-hidden className="absolute -top-12 -left-12 w-44 h-44 rounded-full blur-3xl opacity-50"
                   style={{ background: 'radial-gradient(closest-side, rgba(244,114,182,0.30), transparent)' }} />
              <div aria-hidden className="absolute -bottom-12 -right-12 w-44 h-44 rounded-full blur-3xl opacity-55"
                   style={{ background: 'radial-gradient(closest-side, rgba(167,139,250,0.30), transparent)' }} />
              <div className="absolute inset-0 hp-grain opacity-40" />

              {/* Eyebrow */}
              <div className="absolute top-5 left-5 text-[8px] font-semibold tracking-[0.22em] text-stone-500 uppercase">
                Atelier · Ed 04 · SS26
              </div>

              {/* Display headline (typeset, not selected). Two-line stack —
                  full canvas width now that the gradient image is gone, so
                  'reverie' renders without clipping. */}
              <div className="absolute top-10 left-5 right-5">
                <div
                  className="font-serif italic font-semibold leading-[0.92] text-stone-900 tracking-tight"
                  style={{ fontSize: '54px' }}
                >
                  Soft<br/>
                  <span className="block">reverie<span className="text-rose-400">.</span></span>
                </div>
              </div>

              {/* Body line */}
              <div className="absolute top-[64%] left-5 right-5 text-[10px] leading-[1.45] text-stone-700">
                Stories from the studio.<br/>
                Soft palettes, slow mornings.
              </div>

              {/* Brand mark bottom-left */}
              <div className="absolute bottom-5 left-5 flex items-center gap-1.5">
                <div className="w-5 h-5 rounded-md bg-stone-900 flex items-center justify-center">
                  <span className="text-white text-[7.5px] font-bold tracking-tight">iV</span>
                </div>
                <span className="text-[9px] font-semibold text-stone-700 tracking-tight">iVisora</span>
              </div>

              {/* CTA bottom-right */}
              <div className="absolute bottom-5 right-5 inline-flex items-center gap-1 text-[9px] font-semibold text-stone-700">
                Read the issue
                <ArrowRight className="w-3 h-3 text-stone-700" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Floating AI action cards */}
    <FloatingAICard
      icon={Sparkles}
      title="AI · Background"
      body="Removed cleanly · 100%"
      className="-left-6 -top-4 sm:-left-12 sm:-top-6"
      animationClass="hp-float-y"
      accentClass="from-violet-500 to-fuchsia-500"
    />
    {/* The "wow" moment — larger active generation card */}
    <AIGenerationCard
      className="-right-6 top-[42%] sm:-right-14"
      animationClass="hp-float-y-2"
    />
    <FloatingAICard
      icon={Maximize2}
      title="Magic resize"
      body="Story 1080 × 1920 · ready"
      className="-bottom-4 right-6 sm:-bottom-7 sm:right-14"
      animationClass="hp-float-y-3"
      accentClass="from-sky-500 to-violet-500"
    />
  </div>
);

/* ───────────── Sections ───────────────────────────────────────────────────── */

const HeroSection = ({ onPrimaryCta, onSecondaryCta }) => (
  <section className="relative pt-12 pb-20 lg:pt-20 lg:pb-28 overflow-hidden">
    <HeroBackdrop />
    <div className="relative container mx-auto px-6 lg:px-8 max-w-[1320px]">
      {/* 11/12 left, 13/12 right — adds up to 12 visually but shifts the
          mockup slightly left of the col-7 default so the composition
          reads more centered against the heading. */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-6 items-center">
        {/* Left column — bumped to col-6 for a more balanced split */}
        <div className="lg:col-span-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/70 backdrop-blur border border-violet-100 text-[12px] font-medium text-violet-700">
            <span className="w-1.5 h-1.5 rounded-full bg-violet-500 hp-pulse-soft" />
            The AI creative platform
          </div>

          <h1 className="mt-5 font-extrabold tracking-tight text-slate-900 text-5xl sm:text-6xl lg:text-7xl leading-[1.05]">
            Create without{' '}
            <span className="font-serif italic font-semibold bg-gradient-to-r from-violet-600 via-fuchsia-500 to-pink-500 bg-clip-text text-transparent">limits</span>.
          </h1>

          <p className="mt-5 text-lg text-slate-600 max-w-md leading-relaxed">
            iVisora is a modern AI-powered creative platform for creators, designers,
            editors, and content teams.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <button
              onClick={onPrimaryCta}
              className="inline-flex items-center gap-2 h-12 px-6 rounded-full font-semibold text-white bg-gradient-to-r from-violet-600 to-fuchsia-500 shadow-[0_14px_40px_-12px_rgba(124,58,237,0.6)] hover:shadow-[0_18px_50px_-12px_rgba(124,58,237,0.75)] hover:-translate-y-0.5 transition-all duration-200"
            >
              Start Creating
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={onSecondaryCta}
              className="inline-flex items-center gap-2 h-12 px-6 rounded-full font-semibold text-slate-700 bg-white/70 backdrop-blur border border-slate-200 hover:border-slate-300 hover:bg-white transition-all duration-200"
            >
              Explore Templates
              <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Right column — canvas mockup */}
        {/* Right column — col-6 for balance; mockup left-aligned within
            its column so it doesn't drift to the right edge. */}
        <div className="lg:col-span-6 relative">
          <HeroCanvasMockup />
        </div>
      </div>
    </div>
  </section>
);

/* ─── Feature highlight strip ──────────────────────────────────────────────── */

const FeatureStrip = () => {
  const items = [
    { icon: Sparkles, label: 'AI Powered' },
    { icon: LayoutGrid, label: 'Smart Templates' },
    { icon: Cloud, label: 'Cloud Sync' },
    { icon: Palette, label: 'Brand Kit' },
    { icon: Users, label: 'Team Ready' },
  ];
  return (
    <section className="relative py-10">
      <div className="container mx-auto px-6 lg:px-8 max-w-6xl">
        <div className="relative">
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4 py-6">
            {items.map((it, idx) => (
              <React.Fragment key={it.label}>
                <div className="flex items-center gap-2.5 text-slate-600">
                  <it.icon className="w-4 h-4 text-violet-500" />
                  <span className="text-sm font-medium">{it.label}</span>
                </div>
                {idx < items.length - 1 && <div className="hidden sm:block h-4 w-px bg-slate-200" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─── Section header (shared) ──────────────────────────────────────────────── */

const SectionHeader = ({ eyebrow, title, italicWord, subtitle, align = 'center' }) => {
  const before = italicWord ? title.split(italicWord)[0] : title;
  const after = italicWord ? title.slice(before.length + italicWord.length) : '';
  return (
    <div className={`max-w-3xl ${align === 'center' ? 'mx-auto text-center' : ''}`}>
      {eyebrow && (
        <div className="text-[11px] tracking-[0.18em] font-semibold text-violet-600 uppercase mb-3">{eyebrow}</div>
      )}
      <h2 className="font-extrabold tracking-tight text-slate-900 text-4xl sm:text-5xl leading-[1.1]">
        {before}
        {italicWord && (
          <span className="font-serif italic font-semibold bg-gradient-to-r from-violet-600 to-fuchsia-500 bg-clip-text text-transparent">{italicWord}</span>
        )}
        {after}
      </h2>
      {subtitle && <p className="mt-4 text-lg text-slate-600 leading-relaxed">{subtitle}</p>}
    </div>
  );
};

/* ─── Templates section ────────────────────────────────────────────────────── */

/* A curated showcase of seven templates rendered in CSS — featured tall,
 * two medium, two landscape, two smaller support. Each has a distinct
 * visual identity so the section reads as a real product gallery. */

/* FEATURED — AI Studio: dark cosmic AI campaign, tall portrait. */
const TplAIStudio = () => (
  <div className="absolute inset-0 overflow-hidden">
    {/* Deep gradient base */}
    <div className="absolute inset-0"
         style={{ background: 'linear-gradient(160deg, #0b0420 0%, #1e1041 35%, #4c1d95 75%, #7e22ce 100%)' }} />
    {/* Soft cosmic blooms */}
    <div aria-hidden className="absolute top-[18%] -right-16 w-72 h-72 rounded-full blur-3xl opacity-60"
         style={{ background: 'radial-gradient(closest-side, rgba(192,132,252,0.55), transparent)' }} />
    <div aria-hidden className="absolute bottom-[10%] -left-16 w-64 h-64 rounded-full blur-3xl opacity-55"
         style={{ background: 'radial-gradient(closest-side, rgba(244,114,182,0.45), transparent)' }} />
    <div aria-hidden className="absolute top-[40%] left-[35%] w-32 h-32 rounded-full blur-3xl opacity-40"
         style={{ background: 'radial-gradient(closest-side, rgba(56,189,248,0.5), transparent)' }} />
    <div className="absolute inset-0 hp-grain opacity-50" />

    {/* Subtle orbital ring */}
    <div aria-hidden className="absolute top-[24%] left-1/2 -translate-x-1/2 w-[78%] aspect-[1.6/1] rounded-full border border-white/10" />
    <div aria-hidden className="absolute top-[34%] left-1/2 -translate-x-1/2 w-[60%] aspect-[1.6/1] rounded-full border border-white/8" />

    <div className="absolute inset-0 p-7 flex flex-col text-white">
      {/* Featured badge */}
      <div className="inline-flex w-fit items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/12 backdrop-blur border border-white/20 text-[9.5px] font-semibold tracking-[0.18em] uppercase">
        <span className="w-1 h-1 rounded-full bg-violet-300 hp-pulse-soft" /> Featured
      </div>

      {/* Eyebrow */}
      <div className="mt-auto text-[10px] uppercase tracking-[0.28em] text-white/60 font-semibold">AI Studio · Vol 02</div>

      {/* Big serif italic display */}
      <div className="mt-2 leading-[0.95]">
        <div className="font-serif italic font-semibold text-[40px] tracking-tight">Make worlds</div>
        <div className="text-[40px] font-extrabold tracking-tight"
             style={{ background: 'linear-gradient(90deg, #fbcfe8, #c4b5fd 50%, #93c5fd)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          you've never
        </div>
        <div className="font-serif italic font-semibold text-[40px] tracking-tight">seen.</div>
      </div>

      {/* Subtitle */}
      <div className="mt-4 text-[11.5px] text-white/75 leading-[1.5] max-w-[88%]">
        A premium capsule of generative templates, prompts, and palettes. Hand-tuned, never generic.
      </div>

      {/* Bottom strip — generation meta */}
      <div className="mt-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shadow">
            <Sparkles className="w-3.5 h-3.5 text-white" />
          </div>
          <div className="leading-tight">
            <div className="text-[9.5px] uppercase tracking-widest text-white/70 font-semibold">Generated</div>
            <div className="text-[10.5px] text-white/95 font-semibold tracking-tight">4096 × 5120 · v2</div>
          </div>
        </div>
        <div className="px-2.5 py-1 rounded-md bg-white/10 backdrop-blur border border-white/15 text-[9.5px] font-semibold tracking-wider">EXPLORE →</div>
      </div>
    </div>
  </div>
);

/* MED A — Soft Reverie editorial (cream/lavender), square. */
const TplSoftReverie = () => (
  <div className="absolute inset-0 overflow-hidden">
    <div className="absolute inset-0"
         style={{ background: 'linear-gradient(135deg, #fdf6ec 0%, #faf3f8 45%, #f1ecf6 100%)' }} />
    <div aria-hidden className="absolute -top-12 -left-12 w-44 h-44 rounded-full blur-3xl opacity-50"
         style={{ background: 'radial-gradient(closest-side, rgba(244,114,182,0.30), transparent)' }} />
    <div aria-hidden className="absolute -bottom-12 -right-12 w-44 h-44 rounded-full blur-3xl opacity-55"
         style={{ background: 'radial-gradient(closest-side, rgba(167,139,250,0.30), transparent)' }} />
    <div className="absolute inset-0 hp-grain opacity-40" />

    <div className="absolute top-5 left-5 text-[9px] font-semibold tracking-[0.22em] text-stone-500 uppercase">
      Atelier · Ed 04 · SS26
    </div>

    <div className="absolute top-12 left-5 right-5">
      <div className="font-serif italic font-semibold leading-[0.92] text-stone-900 tracking-tight"
           style={{ fontSize: '60px' }}>
        Soft<br/>
        reverie<span className="text-rose-400">.</span>
      </div>
    </div>

    <div className="absolute bottom-16 left-5 right-5 text-[11px] leading-[1.5] text-stone-700">
      Stories from the studio.<br/>Soft palettes, slow mornings.
    </div>

    <div className="absolute bottom-5 left-5 flex items-center gap-1.5">
      <div className="w-6 h-6 rounded-md bg-stone-900 flex items-center justify-center">
        <span className="text-white text-[8px] font-bold tracking-tight">iV</span>
      </div>
      <span className="text-[10px] font-semibold text-stone-700 tracking-tight">iVisora</span>
    </div>

    <div className="absolute bottom-5 right-5 inline-flex items-center gap-1 text-[10px] font-semibold text-stone-700">
      Read the issue <ArrowRight className="w-3 h-3" />
    </div>
  </div>
);

/* MED B — Maison Vespera: black + champagne luxury fashion editorial, square. */
const TplMaisonLuxury = () => (
  <div className="absolute inset-0 overflow-hidden">
    <div className="absolute inset-0"
         style={{ background: 'radial-gradient(ellipse at 70% 20%, #2a1d12 0%, #100a06 60%, #050302 100%)' }} />
    {/* champagne bloom */}
    <div aria-hidden className="absolute -top-10 -right-10 w-44 h-44 rounded-full blur-3xl opacity-60"
         style={{ background: 'radial-gradient(closest-side, rgba(212,175,124,0.45), transparent)' }} />
    <div className="absolute inset-0 hp-grain opacity-50" />

    <div className="absolute top-5 left-5 text-[9px] font-semibold tracking-[0.32em] uppercase"
         style={{ color: '#d4af7c' }}>
      Maison · Capsule 04
    </div>

    <div className="absolute top-12 left-5 right-5">
      <div className="font-serif italic font-medium leading-[0.92] tracking-tight"
           style={{ fontSize: '62px', color: '#f5e7c7' }}>
        Vespera<span style={{ color: '#d4af7c' }}>.</span>
      </div>
      <div className="mt-2 text-[10px] tracking-[0.18em] uppercase font-semibold" style={{ color: '#9c7d54' }}>
        Quiet luxury · 26
      </div>
    </div>

    <div className="absolute bottom-16 left-5 right-[40%] text-[11px] leading-[1.5]"
         style={{ color: 'rgba(245,231,199,0.7)' }}>
      A capsule for the slow hour.<br/>Eight pieces. One mood.
    </div>

    {/* Right gold ornament */}
    <div aria-hidden className="absolute right-5 top-[55%] w-12 h-12 rounded-full"
         style={{ background: 'conic-gradient(from 220deg, #d4af7c, #f5e7c7, #9c7d54, #d4af7c)', boxShadow: '0 8px 20px -8px rgba(212,175,124,0.45)' }} />

    <div className="absolute bottom-5 left-5 flex items-center gap-1.5"
         style={{ color: '#d4af7c' }}>
      <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ border: '1px solid #d4af7c' }}>
        <span className="text-[8px] font-bold font-serif italic">M</span>
      </div>
      <span className="text-[10px] font-semibold tracking-[0.18em] uppercase">Maison Lux</span>
    </div>

    <div className="absolute bottom-5 right-5 inline-flex items-center gap-1 text-[10px] font-semibold tracking-[0.18em] uppercase"
         style={{ color: '#d4af7c' }}>
      View capsule <ArrowRight className="w-3 h-3" />
    </div>
  </div>
);

/* LAND A — Pitch Velocity: clean dual-tone modern startup pitch, 16/9. */
const TplPitchVelocity = () => (
  <div className="absolute inset-0 bg-white">
    {/* Left accent column with subtle gradient + slow grid */}
    <div className="absolute left-0 top-0 bottom-0 w-[36%] overflow-hidden"
         style={{ background: 'linear-gradient(180deg, #0f172a 0%, #1e1b4b 100%)' }}>
      <div className="absolute inset-0 hp-grain opacity-60" />
      <div aria-hidden className="absolute -top-8 -left-8 w-36 h-36 rounded-full blur-3xl opacity-50"
           style={{ background: 'radial-gradient(closest-side, rgba(192,132,252,0.55), transparent)' }} />
      <div className="absolute top-5 left-5 text-[9px] font-semibold tracking-[0.22em] uppercase text-violet-300/90">
        Series A
      </div>
      <div className="absolute bottom-5 left-5 right-5 flex items-center gap-2 text-white">
        <div className="w-7 h-7 rounded-md bg-white/10 backdrop-blur border border-white/15 flex items-center justify-center">
          <span className="text-[10px] font-bold tracking-tight">N</span>
        </div>
        <div className="leading-tight">
          <div className="text-[10px] font-semibold tracking-tight">Northwind Labs</div>
          <div className="text-[9px] text-white/60 tracking-wide">Q3 · 2026</div>
        </div>
      </div>
    </div>

    {/* Right content */}
    <div className="absolute left-[36%] right-0 top-0 bottom-0 p-7 flex flex-col">
      <div className="text-[9px] font-semibold tracking-[0.22em] uppercase text-slate-500">Pitch · 04 / 18</div>
      <div className="mt-3 leading-[1.05]">
        <div className="text-[34px] font-extrabold tracking-tight text-slate-900">Less stack.</div>
        <div className="text-[34px] font-serif italic font-semibold tracking-tight text-slate-900">More velocity.</div>
      </div>
      <div className="mt-3 text-[11px] text-slate-600 leading-[1.5] max-w-[90%]">
        A new operating layer for product teams that need to move at the speed of their roadmap — not their meetings.
      </div>

      {/* metric chips */}
      <div className="mt-auto flex items-center gap-2.5">
        <div className="px-3 py-1.5 rounded-lg bg-violet-50 border border-violet-100">
          <div className="text-[9px] font-semibold text-violet-700 tracking-wider uppercase">ARR</div>
          <div className="text-[14px] font-extrabold text-slate-900 tracking-tight">$4.2M</div>
        </div>
        <div className="px-3 py-1.5 rounded-lg bg-slate-50 border border-slate-200">
          <div className="text-[9px] font-semibold text-slate-600 tracking-wider uppercase">NPS</div>
          <div className="text-[14px] font-extrabold text-slate-900 tracking-tight">72</div>
        </div>
        <div className="px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-100">
          <div className="text-[9px] font-semibold text-emerald-700 tracking-wider uppercase">Growth</div>
          <div className="text-[14px] font-extrabold text-slate-900 tracking-tight">+38%</div>
        </div>
      </div>
    </div>
  </div>
);

/* LAND B — Resonate: dark neon event poster, 16/9. */
const TplResonate = () => (
  <div className="absolute inset-0 overflow-hidden text-white">
    <div className="absolute inset-0"
         style={{ background: 'radial-gradient(ellipse at 70% 30%, #4c1d95 0%, #1e1b4b 55%, #050111 100%)' }} />
    <div aria-hidden className="absolute -top-10 left-[30%] w-56 h-56 rounded-full blur-3xl opacity-60"
         style={{ background: 'radial-gradient(closest-side, rgba(244,114,182,0.55), transparent)' }} />
    <div aria-hidden className="absolute -bottom-12 right-[8%] w-56 h-56 rounded-full blur-3xl opacity-55"
         style={{ background: 'radial-gradient(closest-side, rgba(56,189,248,0.45), transparent)' }} />
    <div className="absolute inset-0 hp-grain opacity-60" />

    {/* sound-wave bars on the right */}
    <div className="absolute right-6 top-1/2 -translate-y-1/2 flex items-end gap-0.5 h-12">
      {[0.4, 0.7, 1, 0.55, 0.85, 0.3, 0.65, 0.95, 0.5, 0.75].map((h, i) => (
        <div key={i} className="w-1 rounded-sm"
             style={{ height: `${h * 100}%`, background: 'linear-gradient(180deg, #f0abfc, #7c3aed)' }} />
      ))}
    </div>

    <div className="absolute inset-0 p-7 flex flex-col">
      <div className="text-[9.5px] font-semibold tracking-[0.32em] uppercase text-white/70">
        Night 03 · After-Hours
      </div>
      <div className="mt-3 leading-[0.95]">
        <div className="font-extrabold tracking-tight text-[44px]"
             style={{ background: 'linear-gradient(90deg, #f0abfc, #c4b5fd 50%, #93c5fd)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', textShadow: '0 0 30px rgba(192,132,252,0.35)' }}>
          RESONATE
        </div>
      </div>
      <div className="mt-2 text-[11px] text-white/75 leading-[1.45] max-w-[58%]">
        A late-night live set with NOVA · Hosted at The Foundry · 9 PM till the lights come up.
      </div>
      <div className="mt-auto flex items-end gap-3 text-[10px] tracking-[0.16em] uppercase">
        <span className="text-white/60">04 / 26 / 26</span>
        <span className="w-px h-3 bg-white/20" />
        <span className="text-white/60">Foundry, BK</span>
        <span className="ml-auto px-3 py-1.5 rounded-full text-[9px] tracking-[0.18em] font-semibold"
              style={{ background: 'linear-gradient(135deg, #ec4899, #7c3aed)', boxShadow: '0 8px 20px -8px rgba(124,58,237,0.6)' }}>
          GET TICKETS
        </span>
      </div>
    </div>
  </div>
);

/* SMALL A — Essays Monthly: minimalist editorial typography, 2/1 wide. */
const TplEssaysMonthly = () => (
  <div className="absolute inset-0 overflow-hidden bg-[#f6f4ee]">
    <div className="absolute inset-0 hp-grain opacity-40" />
    {/* small color block */}
    <div aria-hidden className="absolute right-6 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full"
         style={{ background: 'radial-gradient(circle at 30% 30%, #f97316, #c2410c)', boxShadow: '0 10px 22px -10px rgba(194,65,12,0.45)' }} />

    <div className="absolute inset-0 p-6 flex items-center">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.3em] text-stone-500 uppercase">Issue · 04 · Spring 26</div>
        <div className="mt-1 leading-[0.92]">
          <span className="font-serif italic text-stone-900" style={{ fontSize: '46px' }}>Essays</span>
          <span className="font-extrabold text-stone-900 tracking-tight ml-2" style={{ fontSize: '46px' }}>monthly.</span>
        </div>
        <div className="mt-2 text-[11px] text-stone-600 leading-snug max-w-[85%]">
          Long reads from designers we admire — paced for a slow Sunday morning.
        </div>
      </div>
    </div>
  </div>
);

/* SMALL B — Spring Drops: soft fashion social post, 2/1 wide. */
const TplSpringDrops = () => (
  <div className="absolute inset-0 overflow-hidden">
    <div className="absolute inset-0"
         style={{ background: 'linear-gradient(120deg, #ffe4e6 0%, #fbcfe8 40%, #ddd6fe 100%)' }} />
    <div aria-hidden className="absolute -top-8 -right-8 w-40 h-40 rounded-full blur-2xl opacity-55"
         style={{ background: 'radial-gradient(closest-side, rgba(251,113,133,0.45), transparent)' }} />
    <div aria-hidden className="absolute bottom-2 left-[30%] w-24 h-24 rounded-full blur-2xl opacity-55"
         style={{ background: 'radial-gradient(closest-side, rgba(167,139,250,0.45), transparent)' }} />

    <div className="absolute inset-0 p-6 flex items-center justify-between">
      <div>
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/80 backdrop-blur border border-white/70 text-[9px] font-semibold tracking-[0.18em] uppercase text-rose-700">
          <span className="w-1 h-1 rounded-full bg-rose-500 hp-pulse-soft" /> New Drop
        </div>
        <div className="mt-2 leading-[0.95]">
          <div className="font-extrabold tracking-tight text-stone-900" style={{ fontSize: '32px' }}>Spring drops</div>
          <div className="font-serif italic font-medium text-stone-900" style={{ fontSize: '32px' }}>are here.</div>
        </div>
        <div className="mt-2 text-[11px] text-stone-700 leading-snug max-w-[85%]">
          Soft tones, breezy fits, and a lookbook to match. Limited release.
        </div>
      </div>
      {/* swatch trio */}
      <div className="flex flex-col gap-1.5 ml-3 flex-shrink-0">
        <div className="w-9 h-9 rounded-xl shadow" style={{ background: 'linear-gradient(135deg, #fda4af, #fb7185)' }} />
        <div className="w-9 h-9 rounded-xl shadow" style={{ background: 'linear-gradient(135deg, #ddd6fe, #a78bfa)' }} />
        <div className="w-9 h-9 rounded-xl shadow" style={{ background: 'linear-gradient(135deg, #fde68a, #fbcfe8)' }} />
      </div>
    </div>
  </div>
);

/* The 7 curated cards — order matters: featured, two medium, two landscape, two small. */
const HOME_TEMPLATES = [
  { id: 'tpl-ai-studio',     name: 'AI Studio · Vol 02',   category: 'social',       slot: 'featured', Component: TplAIStudio },
  { id: 'tpl-soft-reverie',  name: 'Soft Reverie',         category: 'branding',     slot: 'medium',   Component: TplSoftReverie },
  { id: 'tpl-maison',        name: 'Maison Vespera',       category: 'branding',     slot: 'medium',   Component: TplMaisonLuxury },
  { id: 'tpl-pitch',         name: 'Pitch Velocity',       category: 'presentation', slot: 'landscape',Component: TplPitchVelocity },
  { id: 'tpl-resonate',      name: 'Resonate · Night 03',  category: 'poster',       slot: 'landscape',Component: TplResonate },
  { id: 'tpl-essays',        name: 'Essays Monthly',       category: 'branding',     slot: 'small',    Component: TplEssaysMonthly },
  { id: 'tpl-spring-drops',  name: 'Spring Drops',         category: 'social',       slot: 'small',    Component: TplSpringDrops },
];

const TEMPLATE_CATEGORIES = [
  { key: 'all',          label: 'All' },
  { key: 'social',       label: 'Social Media' },
  { key: 'presentation', label: 'Presentations' },
  { key: 'branding',     label: 'Marketing' },
  { key: 'poster',       label: 'Print' },
  { key: 'video',        label: 'Video' },
];

/* Reusable card wrapper — handles hover interactions for any template body. */
const TemplateCard = ({ t, onUseTemplate, aspect }) => (
  <div className="relative h-full">
    <div
      aria-hidden
      className="hp-tplcard-glow absolute -inset-2 rounded-[2rem] pointer-events-none -z-10 blur-2xl"
      style={{ background: 'radial-gradient(closest-side, rgba(167,139,250,0.45), transparent 70%)' }}
    />
    <button
      onClick={() => onUseTemplate(t)}
      className="hp-tplcard relative block w-full h-full text-left rounded-3xl bg-white overflow-hidden group"
      style={{ boxShadow: '0 1px 0 0 rgba(255,255,255,0.9) inset, 0 18px 40px -22px rgba(15,23,42,0.18), 0 4px 10px -6px rgba(15,23,42,0.10)' }}
    >
      <div className="hp-tplcard-ring absolute inset-0 rounded-3xl pointer-events-none" />
      <div className="hp-tplcard-inner relative w-full" style={aspect ? { aspectRatio: aspect } : { height: '100%' }}>
        <t.Component />
      </div>
      <div className="hp-tplcard-overlay absolute left-4 right-4 bottom-4 flex items-center justify-between">
        <div className="px-3 py-1.5 rounded-full backdrop-blur-md bg-white/90 border border-white/70 text-[12px] font-medium text-slate-800 shadow">
          {t.name}
        </div>
        <div
          className="px-3 py-1.5 rounded-full text-white text-[12px] font-semibold flex items-center gap-1.5"
          style={{ background: 'linear-gradient(135deg, #7c3aed, #ec4899)', boxShadow: '0 8px 18px -8px rgba(124,58,237,0.55)' }}
        >
          Use Template <ArrowRight className="w-3.5 h-3.5" />
        </div>
      </div>
    </button>
  </div>
);

const TemplatesSection = ({ onUseTemplate, onBrowseAll }) => {
  const [active, setActive] = useState('all');

  const showCurated = active === 'all';
  const filtered = useMemo(() => {
    if (showCurated) return HOME_TEMPLATES;
    const matched = HOME_TEMPLATES.filter(t => t.category === active);
    return matched.length ? matched : HOME_TEMPLATES;
  }, [active, showCurated]);

  // Map curated entries by slot for the editorial grid.
  const featured = HOME_TEMPLATES.find(t => t.slot === 'featured');
  const mediums = HOME_TEMPLATES.filter(t => t.slot === 'medium');
  const landscapes = HOME_TEMPLATES.filter(t => t.slot === 'landscape');
  const smalls = HOME_TEMPLATES.filter(t => t.slot === 'small');

  return (
    <section className="relative py-20 md:py-24">
      <div className="container mx-auto px-6 lg:px-8 max-w-[1320px]">
        <SectionHeader
          eyebrow="Templates"
          title="Made for every idea you'll ever have"
          italicWord="every"
          subtitle="A curated set of premium designs — pick one, change a heading, you're done."
        />

        {/* Category pill bar — horizontally scrollable on mobile, centered on desktop */}
        <div className="relative mt-10">
          <div className="md:flex md:justify-center">
            <div className="flex md:flex-wrap gap-2 overflow-x-auto md:overflow-visible -mx-6 px-6 md:mx-0 md:px-0 snap-x snap-mandatory hp-scrollbar-hide">
              {TEMPLATE_CATEGORIES.map((c) => {
                const isActive = active === c.key;
                return (
                  <button
                    key={c.key}
                    onClick={() => setActive(c.key)}
                    className={`flex-shrink-0 snap-start whitespace-nowrap h-10 px-4 rounded-full text-[13.5px] font-medium tracking-tight transition-all duration-200 ${
                      isActive
                        ? 'text-white shadow-[0_10px_24px_-10px_rgba(124,58,237,0.6)]'
                        : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-300 hover:text-slate-900'
                    }`}
                    style={isActive ? { background: 'linear-gradient(135deg, #7c3aed, #a855f7 50%, #ec4899)' } : undefined}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="md:hidden pointer-events-none absolute left-0 top-0 bottom-0 w-6 bg-gradient-to-r from-white to-transparent" />
          <div className="md:hidden pointer-events-none absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-white to-transparent" />
        </div>

        {showCurated ? (
          /* Curated editorial grid — featured + 2 medium + 2 landscape + 2 small */
          <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-7">
            {/* Featured: tall, left, spans rows 1-2 on lg.
                Aspect 7/10 lines up exactly with two stacked square mediums. */}
            <div className="lg:col-span-7 lg:row-span-2 relative">
              <div className="aspect-[4/5] lg:aspect-[7/10]">
                <TemplateCard t={featured} onUseTemplate={onUseTemplate} />
              </div>
            </div>

            {/* Medium A — square, top-right */}
            <div className="lg:col-span-5">
              <div className="aspect-square">
                <TemplateCard t={mediums[0]} onUseTemplate={onUseTemplate} />
              </div>
            </div>

            {/* Medium B — square, mid-right (under medium A) */}
            <div className="lg:col-span-5">
              <div className="aspect-square">
                <TemplateCard t={mediums[1]} onUseTemplate={onUseTemplate} />
              </div>
            </div>

            {/* Landscape A */}
            <div className="lg:col-span-6">
              <div className="aspect-[16/9]">
                <TemplateCard t={landscapes[0]} onUseTemplate={onUseTemplate} />
              </div>
            </div>
            {/* Landscape B */}
            <div className="lg:col-span-6">
              <div className="aspect-[16/9]">
                <TemplateCard t={landscapes[1]} onUseTemplate={onUseTemplate} />
              </div>
            </div>

            {/* Small A — wide-short support card */}
            <div className="lg:col-span-6">
              <div className="aspect-[2.4/1]">
                <TemplateCard t={smalls[0]} onUseTemplate={onUseTemplate} />
              </div>
            </div>
            {/* Small B */}
            <div className="lg:col-span-6">
              <div className="aspect-[2.4/1]">
                <TemplateCard t={smalls[1]} onUseTemplate={onUseTemplate} />
              </div>
            </div>
          </div>
        ) : (
          /* Filtered view — simple uniform grid that adapts to fewer cards. */
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((t) => (
              <div key={t.id} className="aspect-[4/3]">
                <TemplateCard t={t} onUseTemplate={onUseTemplate} />
              </div>
            ))}
          </div>
        )}

        <div className="mt-12 flex justify-center">
          <button
            onClick={onBrowseAll}
            className="inline-flex items-center gap-2 h-12 px-6 rounded-full font-semibold text-slate-700 bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors"
          >
            Browse all templates
            <ArrowUpRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

/* ─── AI Tools section ─────────────────────────────────────────────────────── */

const ToolBadge = ({ icon: Icon, accent = 'from-violet-500 to-fuchsia-500' }) => (
  <div className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${accent} flex items-center justify-center text-white shadow-[0_10px_24px_-12px_rgba(124,58,237,0.6)]`}>
    <Icon className="w-5 h-5" />
  </div>
);

const FeaturedToolCard = () => (
  <div className="relative h-full rounded-3xl bg-white border border-slate-100 shadow-[0_20px_50px_-25px_rgba(76,29,149,0.25)] p-7 overflow-hidden group">
    {/* Soft corner gradient */}
    <div aria-hidden className="absolute -top-20 -right-20 w-72 h-72 rounded-full blur-3xl opacity-50"
         style={{ background: 'radial-gradient(closest-side, rgba(244,114,182,0.4), transparent)' }} />
    <div className="relative">
      <ToolBadge icon={ImageIcon} accent="from-fuchsia-500 to-violet-500" />
      <h3 className="mt-5 text-xl font-bold text-slate-900">Text to Image</h3>
      <p className="mt-2 text-slate-600 text-sm leading-relaxed max-w-xs">
        Describe what you imagine. Get original visuals shaped to your style and brand.
      </p>

      {/* Mini before → after */}
      <div className="mt-6 flex items-center gap-3">
        <div className="flex-1 h-10 rounded-xl bg-white border border-slate-200 px-3 flex items-center text-[12px] text-slate-500">
          <span className="truncate">"a soft pastel landscape, golden hour…"</span>
          <span className="ml-1 hp-caret w-px h-4 bg-violet-500 self-center" />
        </div>
        <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
        <div className="w-16 h-16 rounded-xl shadow-md flex-shrink-0 overflow-hidden"
             style={{ background: 'conic-gradient(from 200deg at 50% 50%, #fde68a, #f9a8d4, #c4b5fd, #93c5fd, #fde68a)' }} />
      </div>

      <div className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-violet-600 group-hover:gap-2.5 transition-all">
        Try it now <ArrowRight className="w-3.5 h-3.5" />
      </div>
    </div>
  </div>
);

const ToolCardWriter = () => (
  <div className="rounded-3xl bg-white border border-slate-100 shadow-[0_20px_50px_-25px_rgba(76,29,149,0.18)] p-6 h-full">
    <ToolBadge icon={Type} accent="from-violet-500 to-sky-500" />
    <h3 className="mt-4 text-lg font-bold text-slate-900">AI Writer</h3>
    <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">Headlines, captions, and long-form drafts that actually sound like you.</p>
    <div className="mt-5 rounded-xl bg-slate-50/70 border border-slate-100 p-3">
      <div className="text-[12.5px] text-slate-700 leading-relaxed">
        <span className="font-medium">Headline · </span>
        Make today's launch unforgettable<span className="hp-caret inline-block w-px h-3.5 bg-violet-500 align-text-bottom ml-0.5" />
      </div>
      <div className="mt-2 flex gap-1.5">
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-50 text-violet-700">+ Short</span>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Casual</span>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Bold</span>
      </div>
    </div>
  </div>
);

const ToolCardResize = () => (
  <div className="rounded-3xl bg-white border border-slate-100 shadow-[0_20px_50px_-25px_rgba(76,29,149,0.18)] p-6 h-full">
    <ToolBadge icon={Maximize2} accent="from-sky-500 to-violet-500" />
    <h3 className="mt-4 text-lg font-bold text-slate-900">Magic Resize</h3>
    <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">One click, every aspect ratio. Layout intelligence rebuilds the design.</p>
    <div className="mt-5 flex items-end gap-2">
      <div className="w-10 h-10 rounded-md bg-violet-100 border border-violet-200" />
      <div className="w-12 h-7 rounded-md bg-violet-200 border border-violet-300" />
      <div className="w-7 h-14 rounded-md bg-violet-300 border border-violet-400" />
      <div className="w-14 h-10 rounded-md bg-gradient-to-br from-violet-500 to-fuchsia-500" />
    </div>
  </div>
);

const ToolCardBgRemover = () => (
  <div className="rounded-3xl bg-white border border-slate-100 shadow-[0_20px_50px_-25px_rgba(76,29,149,0.18)] p-6 h-full">
    <ToolBadge icon={Eraser} accent="from-emerald-500 to-violet-500" />
    <h3 className="mt-4 text-lg font-bold text-slate-900">Background Remover</h3>
    <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">Crisp cutouts, hair and all. Keep the subject, drop everything else.</p>
    <div className="mt-5 relative h-14 rounded-xl overflow-hidden border border-slate-100"
         style={{ background: 'repeating-conic-gradient(rgba(15,23,42,0.06) 0% 25%, transparent 0% 50%) 50% / 14px 14px' }}>
      <div className="absolute left-3 top-1.5 w-11 h-11 rounded-full bg-gradient-to-br from-amber-200 to-rose-200 shadow" />
    </div>
  </div>
);

const ToolCardMockup = () => (
  <div className="rounded-3xl bg-white border border-slate-100 shadow-[0_20px_50px_-25px_rgba(76,29,149,0.18)] p-6 h-full">
    <ToolBadge icon={Layers} accent="from-fuchsia-500 to-rose-500" />
    <h3 className="mt-4 text-lg font-bold text-slate-900">Mockup Generator</h3>
    <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">Drop a design onto a phone, laptop, or poster. Real perspective, instantly.</p>
    <div className="mt-5 relative h-14">
      <div className="absolute left-1 top-1 w-12 h-12 rounded-md bg-slate-100 border border-slate-200 -rotate-6" />
      <div className="absolute left-7 top-0 w-12 h-12 rounded-md bg-gradient-to-br from-violet-200 to-fuchsia-200 border border-violet-200 rotate-3 shadow" />
      <div className="absolute left-14 top-2 w-12 h-12 rounded-md bg-gradient-to-br from-violet-500 to-fuchsia-500 -rotate-2 shadow-md" />
    </div>
  </div>
);

const AIToolsSection = () => (
  <section id="ai-tools" className="relative py-20 md:py-24 overflow-hidden">
    {/* Soft section ambience */}
    <div aria-hidden className="absolute inset-0 bg-gradient-to-b from-white via-violet-50/25 to-white pointer-events-none" />
    <div aria-hidden className="absolute -top-32 right-0 w-[640px] h-[640px] rounded-full blur-3xl opacity-30 pointer-events-none"
         style={{ background: 'radial-gradient(closest-side, rgba(167,139,250,0.30), transparent 70%)' }} />
    <div aria-hidden className="absolute inset-0 hp-grain pointer-events-none" />
    <div className="relative container mx-auto px-6 lg:px-8 max-w-[1320px]">
      <SectionHeader
        eyebrow="AI Tools"
        title="Co-create with intelligence"
        italicWord="intelligence"
        subtitle="Tools that read your intent — not just your prompt."
      />

      <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Row 1 */}
        <div className="lg:col-span-7"><FeaturedToolCard /></div>
        <div className="lg:col-span-5"><ToolCardWriter /></div>

        {/* Row 2 */}
        <div className="lg:col-span-4"><ToolCardResize /></div>
        <div className="lg:col-span-4"><ToolCardBgRemover /></div>
        <div className="lg:col-span-4"><ToolCardMockup /></div>
      </div>
    </div>
  </section>
);

/* ─── How It Works ─────────────────────────────────────────────────────────── */

const StepCard = ({ number, title, body, offsetClass, accent = 'from-violet-500 to-fuchsia-500', children }) => (
  <div className={`relative rounded-3xl bg-white border border-slate-100 shadow-[0_20px_50px_-25px_rgba(76,29,149,0.2)] p-7 ${offsetClass}`}>
    <div className="flex items-start justify-between">
      <div className="font-serif italic font-semibold text-5xl bg-gradient-to-br from-violet-600 to-fuchsia-500 bg-clip-text text-transparent leading-none">
        {number}
      </div>
      <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${accent} flex items-center justify-center text-white text-[10px] font-semibold tracking-wide`}>
        STEP
      </div>
    </div>
    <h3 className="mt-6 text-xl font-bold text-slate-900">{title}</h3>
    <p className="mt-2 text-slate-600 text-sm leading-relaxed">{body}</p>
    <div className="mt-6">{children}</div>
  </div>
);

const HowItWorksSection = () => (
  <section className="relative py-20 md:py-24 overflow-hidden">
    {/* Layered ambience — distinct from neighboring sections.
        Faint top-and-bottom violet wash, a soft center bloom, dot grid, then grain. */}
    <div aria-hidden className="absolute inset-0 pointer-events-none"
         style={{ background: 'linear-gradient(180deg, rgba(245,243,255,0.7) 0%, rgba(255,255,255,0) 25%, rgba(255,255,255,0) 75%, rgba(253,244,255,0.6) 100%)' }} />
    <div aria-hidden className="absolute -top-40 left-1/2 -translate-x-1/2 w-[920px] h-[920px] rounded-full blur-3xl opacity-50 pointer-events-none"
         style={{ background: 'radial-gradient(closest-side, rgba(167,139,250,0.28), transparent 70%)' }} />
    <div aria-hidden className="absolute -bottom-40 right-[10%] w-[420px] h-[420px] rounded-full blur-3xl opacity-40 pointer-events-none"
         style={{ background: 'radial-gradient(closest-side, rgba(244,114,182,0.30), transparent 70%)' }} />
    <div aria-hidden className="absolute inset-0 hp-dotgrid opacity-60 pointer-events-none" />
    <div aria-hidden className="absolute inset-0 hp-grain pointer-events-none" />
    <div className="relative container mx-auto px-6 lg:px-8 max-w-[1320px]">
      <SectionHeader
        eyebrow="How it works"
        title="Three steps. Zero friction."
        italicWord="friction"
        subtitle="No tutorials, no manuals. Just press play."
      />

      <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
        {/* Step 1 — three template-card silhouettes with one being "picked" (violet ring) */}
        <StepCard number="01" title="Pick a starting point" body="Browse the template library or start from a blank canvas — both feel premium." offsetClass="md:translate-y-0" accent="from-violet-500 to-fuchsia-500">
          <div className="relative rounded-xl bg-gradient-to-br from-slate-50 to-white border border-slate-100 p-3">
            <div className="grid grid-cols-3 gap-2">
              <div className="aspect-[3/4] rounded-md overflow-hidden" style={{ background: 'linear-gradient(135deg, #fef3c7, #fcd34d)' }}>
                <div className="h-2/5 bg-amber-200" />
                <div className="px-1.5 pt-1 flex flex-col gap-0.5"><div className="h-0.5 rounded bg-amber-700/40 w-3/4" /><div className="h-0.5 rounded bg-amber-700/30 w-1/2" /></div>
              </div>
              <div
                className="aspect-[3/4] rounded-md relative overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, #ddd6fe, #a78bfa)',
                  boxShadow: '0 0 0 2px #7c3aed, 0 6px 14px -6px rgba(124,58,237,0.45)',
                }}
              >
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-6 h-6 rounded-full bg-white/70 backdrop-blur-sm flex items-center justify-center">
                    <ArrowRight className="w-3 h-3 text-violet-700" />
                  </div>
                </div>
              </div>
              <div className="aspect-[3/4] rounded-md overflow-hidden" style={{ background: 'linear-gradient(135deg, #fce7f3, #fbcfe8)' }}>
                <div className="h-1/2 bg-rose-200/70" />
                <div className="px-1.5 pt-1 flex flex-col gap-0.5"><div className="h-0.5 rounded bg-rose-500/30 w-3/4" /><div className="h-0.5 rounded bg-rose-500/20 w-1/2" /></div>
              </div>
            </div>
            <div className="mt-2 flex items-center gap-1.5 text-[10px] text-slate-500">
              <span className="w-1.5 h-1.5 rounded-full bg-violet-500" /> Selected · Marketing post
            </div>
          </div>
        </StepCard>

        {/* Step 2 — a mini canvas with a selected element being resized; cursor + handles */}
        <StepCard number="02" title="Make it yours" body="Drag, drop, retype, or hand it to AI. Every change feels weightless." offsetClass="md:translate-y-4" accent="from-fuchsia-500 to-pink-500">
          <div className="relative rounded-xl border border-slate-100 h-24 overflow-hidden"
               style={{ background: 'radial-gradient(ellipse at 30% 30%, #ffffff, #f4f5fb)' }}>
            <div
              aria-hidden
              className="absolute inset-0 opacity-50"
              style={{ backgroundImage: 'radial-gradient(circle, rgba(15,23,42,0.06) 1px, transparent 1px)', backgroundSize: '10px 10px' }}
            />
            <div className="absolute left-3 top-3 bottom-3 w-[58%] rounded-lg bg-white border border-slate-200 p-2">
              <div className="h-2 w-3/4 rounded bg-slate-200" />
              <div className="mt-1 h-1.5 w-1/2 rounded bg-slate-100" />
            </div>
            {/* Selected image element with handles + dragging cursor */}
            <div className="absolute right-4 top-4 w-12 h-12 rounded-md"
                 style={{ background: 'linear-gradient(135deg, #c4b5fd, #f9a8d4)', boxShadow: '0 0 0 1.5px #7c3aed, 0 0 0 4px rgba(124,58,237,0.16)' }}>
              <div className="absolute -top-[3px] -left-[3px] w-1.5 h-1.5 rounded-[1.5px] bg-white border border-violet-600" />
              <div className="absolute -top-[3px] -right-[3px] w-1.5 h-1.5 rounded-[1.5px] bg-white border border-violet-600" />
              <div className="absolute -bottom-[3px] -left-[3px] w-1.5 h-1.5 rounded-[1.5px] bg-white border border-violet-600" />
              <div className="absolute -bottom-[3px] -right-[3px] w-1.5 h-1.5 rounded-[1.5px] bg-white border border-violet-600" />
            </div>
            {/* Cursor pointer */}
            <svg className="absolute right-2 bottom-2" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 1l9 5-4 1-1 4-4-10z" fill="white" stroke="#0f172a" strokeWidth="0.8" strokeLinejoin="round" />
            </svg>
          </div>
        </StepCard>

        {/* Step 3 — export menu showing format options + a primary download CTA */}
        <StepCard number="03" title="Publish anywhere" body="Export in any format, share a live link, or schedule it straight from the app." offsetClass="md:translate-y-8" accent="from-sky-500 to-violet-500">
          <div className="rounded-xl bg-white border border-slate-100 p-3 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Export</div>
              <div className="text-[10px] text-slate-400">Ready</div>
            </div>
            <div className="mt-2 grid grid-cols-4 gap-1.5">
              {['PDF', 'PNG', 'JPG', 'MP4'].map((f, i) => (
                <div key={f} className={`h-7 rounded-md flex items-center justify-center text-[10.5px] font-bold ${i === 0 ? 'bg-violet-50 text-violet-700 ring-1 ring-violet-200' : 'bg-slate-50 text-slate-600'}`}>{f}</div>
              ))}
            </div>
            <div className="mt-2 flex items-center gap-1.5">
              <div
                className="flex-1 h-7 rounded-md text-white text-[10.5px] font-semibold flex items-center justify-center gap-1"
                style={{ background: 'linear-gradient(135deg, #7c3aed, #ec4899)' }}
              >
                Download <ArrowRight className="w-3 h-3" />
              </div>
              <div className="h-7 px-2 rounded-md border border-slate-200 text-[10.5px] font-semibold text-slate-600 flex items-center">Share</div>
            </div>
          </div>
        </StepCard>
      </div>
    </div>
  </section>
);

/* ─── Final CTA ────────────────────────────────────────────────────────────── */

const FinalCTASection = ({ onSignUp, onStartDesigning }) => (
  <section className="relative py-20 md:py-24">
    <div className="container mx-auto px-6 lg:px-8 max-w-[1320px]">
      <div
        className="relative overflow-hidden rounded-[2.5rem] px-8 py-16 sm:px-14 md:py-24 text-white"
        style={{
          background:
            'linear-gradient(135deg, #4c1d95 0%, #6d28d9 25%, #8b5cf6 55%, #c026d3 80%, #ec4899 100%)',
          boxShadow:
            '0 1px 0 0 rgba(255,255,255,0.18) inset, 0 30px 80px -30px rgba(76,29,149,0.65), 0 12px 30px -16px rgba(15,23,42,0.25)',
        }}
      >
        {/* Top-left bloom */}
        <div aria-hidden className="absolute -top-40 -left-32 w-[520px] h-[520px] rounded-full pointer-events-none"
             style={{ background: 'radial-gradient(closest-side, rgba(255,255,255,0.28), transparent 70%)' }} />
        {/* Bottom-right warm glow */}
        <div aria-hidden className="absolute -bottom-40 -right-24 w-[480px] h-[480px] rounded-full pointer-events-none"
             style={{ background: 'radial-gradient(closest-side, rgba(244,114,182,0.45), transparent 70%)' }} />
        {/* Soft top sheen */}
        <div aria-hidden className="absolute inset-x-0 top-0 h-px pointer-events-none"
             style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)' }} />
        {/* Dot grid + grain */}
        <div aria-hidden className="absolute inset-0 opacity-20 hp-dotgrid pointer-events-none" />
        <div aria-hidden className="absolute inset-0 hp-grain pointer-events-none" />

        <div className="relative max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur border border-white/20 text-[12px] font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-white hp-pulse-soft" />
            Free to try · No credit card
          </div>
          <h2 className="mt-5 font-extrabold tracking-tight text-4xl sm:text-5xl md:text-6xl leading-[1.05]">
            Bring your next idea{' '}
            <span className="font-serif italic font-semibold">to life</span> today.
          </h2>
          <p className="mt-5 text-white/85 text-lg leading-relaxed max-w-xl">
            Join the makers building everything from launch posts to full campaigns — without the busywork.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <button
              onClick={onSignUp}
              className="group inline-flex items-center gap-2 h-12 px-6 rounded-full text-[14.5px] font-semibold text-violet-700 bg-white tracking-tight transition-all duration-300 hover:-translate-y-0.5"
              style={{
                boxShadow:
                  '0 1px 0 0 rgba(255,255,255,0.9) inset, 0 14px 30px -10px rgba(15,23,42,0.35), 0 4px 10px -4px rgba(15,23,42,0.18)',
              }}
            >
              Sign Up
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </button>
            <button
              onClick={onStartDesigning}
              className="group inline-flex items-center gap-2 h-12 px-6 rounded-full text-[14.5px] font-semibold text-white tracking-tight border border-white/35 backdrop-blur-sm bg-white/5 hover:bg-white/10 hover:border-white/50 transition-all duration-300"
            >
              Start Creating
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ───────────── HomePage entry ─────────────────────────────────────────────── */

const HomePage = () => {
  const navigate = useNavigate();
  usePageMeta('iVisora — AI Creative Platform',
    'iVisora — AI Creative Platform for creators, designers, editors, and content teams. Create without limits.',
    { path: '/' });
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authTab, setAuthTab] = useState('signup');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const pendingNav = useRef(null);
  const pendingDesign = useRef(null);

  // If a deeplink lands on /#features etc, scroll to that section after mount
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.replace('#', '');
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, []);

  const isAuthed = () => Boolean(localStorage.getItem(AUTH_TOKEN_KEY) && localStorage.getItem(USER_KEY));

  const goEditor = () => {
    setCreateModalOpen(true);
  };

  const goSignUp = () => {
    if (isAuthed()) navigate('/dashboard');
    else {
      setAuthTab('signup');
      setAuthModalOpen(true);
    }
  };

  const handleCreateDesign = ({ canvasSize, designType }) => {
    setCreateModalOpen(false);
    const state = { canvasSize, designType };
    if (isAuthed()) {
      navigate('/editor', { state });
    } else {
      pendingDesign.current = state;
      setAuthTab('signup');
      setAuthModalOpen(true);
    }
  };

  const handleAuthSuccess = () => {
    setAuthModalOpen(false);
    if (pendingDesign.current) {
      const state = pendingDesign.current;
      pendingDesign.current = null;
      navigate('/editor', { state });
      return;
    }
    if (pendingNav.current) {
      const dest = pendingNav.current;
      pendingNav.current = null;
      navigate(dest);
    }
  };

  // The cards on this page are illustrations, not catalog templates — their ids
  // ('tpl-ai-studio'…) exist nowhere else — so "Use Template" opens the real
  // gallery, where Use Template signs the visitor in and opens the editor.
  const useTemplate = () => navigate('/templates');

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Header />

      <main>
        <HeroSection onPrimaryCta={goEditor} onSecondaryCta={() => navigate('/templates')} />
        <div id="features"><FeatureStrip /></div>
        <TemplatesSection onUseTemplate={useTemplate} onBrowseAll={() => navigate('/templates')} />
        <AIToolsSection />
        <HowItWorksSection />
        <FinalCTASection onSignUp={goSignUp} onStartDesigning={goEditor} />
      </main>

      <Footer />

      <AuthModal
        open={authModalOpen}
        defaultTab={authTab}
        onClose={() => setAuthModalOpen(false)}
        onAuthSuccess={handleAuthSuccess}
      />

      <CreateDesignModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreate={handleCreateDesign}
      />
    </div>
  );
};

export default HomePage;
